import { ApiError } from '../api/client';
import type { Cloudflare502Diagnostic } from '../api/cloudflare502Diagnostic';
import { safeGitlabRequestId } from '../api/gitlabRequestId';

export const GITLAB_TOKEN_REQUEST = 'POST /api/conversations/gitlab/ws-token';
export const GITLAB_SOCKET_REQUEST = 'GET /api/conversations/gitlab/ws';

export interface GitlabUpdatesFailure {
  status?: number;
  request: string;
  requestId?: string;
  contentType?: string;
  cloudflare?: Cloudflare502Diagnostic;
  detail: string;
  retrying: boolean;
}

function safeContentType(value: string | undefined): string | undefined {
  const type = value?.split(';', 1)[0].trim().toLowerCase();
  return type && ['application/json', 'application/problem+json', 'text/html', 'text/plain'].includes(type)
    ? type
    : undefined;
}

export function gitlabTokenFailure(error: unknown, retrying: boolean): GitlabUpdatesFailure {
  if (error instanceof ApiError) {
    return {
      status: error.status,
      request: GITLAB_TOKEN_REQUEST,
      requestId: safeGitlabRequestId(error.requestId),
      contentType: safeContentType(error.contentType),
      cloudflare: error.cloudflare,
      // Error messages and bodies are supplied by the response. A malformed
      // successful token response can also contain other credentials.
      detail: error.status >= 200 && error.status < 300
        ? 'Invalid GitLab update token response.'
        : 'GitLab update token request failed.',
      retrying
    };
  }
  return {
    request: GITLAB_TOKEN_REQUEST,
    detail: 'GitLab update token request failed.',
    retrying
  };
}

export function gitlabWebsocketFailure(
  event: Pick<CloseEvent, 'code' | 'reason' | 'wasClean'> | null,
  requestId: string | null,
  retrying: boolean,
  protocolError?: string
): GitlabUpdatesFailure {
  const detail = protocolError
    ? 'WebSocket reported an error.'
    : event
      ? `WebSocket closed: code ${event.code}; clean ${event.wasClean}`
      : 'WebSocket connection failed.';
  return {
    request: GITLAB_SOCKET_REQUEST,
    requestId: safeGitlabRequestId(requestId),
    detail,
    retrying
  };
}

// Three automatic retries after the first failure. Manual Retry starts a new run.
export function gitlabReconnectDelay(attempts: number): number | null {
  return attempts < 3 ? (attempts + 1) * 1000 : null;
}
